import { FC, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { tenantService } from '@/services/tenantService'
import { TenantAdmin } from '@/types/tenant'
import { CANAIS_COMERCIAIS, CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import { AUDIENCIA_ROTULO, AudienceType, StatusCampanha } from '@/types/campanha'

export interface AudienciaValor {
  audienceType: AudienceType
  audienciaCanais: CanalComercial[]
  audienciaEmpresas: string[]
}

interface Props {
  valor: AudienciaValor
  onChange: (v: AudienciaValor) => void
  /** Status da campanha em edição (nova = undefined). Regras do AN-8 §3. */
  statusAtual?: StatusCampanha
  /** Audiência gravada (edição): ATIVA só amplia e não vira NOMINAL. */
  tipoAtual?: AudienceType
}

const DESCRICAO: Record<AudienceType, string> = {
  TODOS_ATIVOS: 'Todos os consultores ativos da plataforma; quem entrar depois é incluído automaticamente.',
  CANAIS: 'Consultores ativos dos canais escolhidos; quem entrar depois é incluído automaticamente.',
  EMPRESAS: 'Consultores ativos das empresas escolhidas; quem entrar depois é incluído automaticamente.',
  NOMINAL: 'Só os consultores inscritos à mão no detalhe da campanha (aba Participantes).',
}

/**
 * "Quem participa" da campanha (AN-8 §3): tipo de audiência + canais ou empresas. A tela não
 * resolve a audiência nem mostra quantos ela alcança — o backend materializa ao salvar/ativar.
 * Mudanças: RASCUNHO livre; ATIVA só amplia (e nunca vira NOMINAL); ENCERRADA não muda.
 */
export const AudienciaCampos: FC<Props> = ({ valor, onChange, statusAtual, tipoAtual }) => {
  const encerrada = statusAtual === 'ENCERRADA'
  const ativa = statusAtual === 'ATIVA'
  const [empresas, setEmpresas] = useState<TenantAdmin[]>([])
  const [erroEmpresas, setErroEmpresas] = useState<string | null>(null)

  const precisaEmpresas = valor.audienceType === 'EMPRESAS'
  useEffect(() => {
    if (!precisaEmpresas || empresas.length > 0) return
    let vivo = true
    tenantService
      .listar()
      .then((lista) => {
        if (!vivo) return
        setEmpresas(lista ?? [])
        setErroEmpresas(null)
      })
      .catch(() => {
        if (vivo) setErroEmpresas('Não foi possível carregar as empresas')
      })
    return () => {
      vivo = false
    }
  }, [precisaEmpresas, empresas.length])

  const selecionadas = useMemo(
    () =>
      valor.audienciaEmpresas.map(
        (id) => empresas.find((e) => e.id === id) ?? ({ id, nome: id, cnpj: '', ativo: true } as TenantAdmin)
      ),
    [valor.audienciaEmpresas, empresas]
  )

  const alternarCanal = (c: CanalComercial) => {
    const tem = valor.audienciaCanais.includes(c)
    onChange({
      ...valor,
      audienciaCanais: tem ? valor.audienciaCanais.filter((x) => x !== c) : [...valor.audienciaCanais, c],
    })
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Quem participa
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          label="Audiência"
          select
          required
          fullWidth
          value={valor.audienceType}
          disabled={encerrada}
          onChange={(e) => onChange({ ...valor, audienceType: e.target.value as AudienceType })}
          helperText={DESCRICAO[valor.audienceType]}
        >
          {(Object.keys(AUDIENCIA_ROTULO) as AudienceType[]).map((t) => (
            <MenuItem
              key={t}
              value={t}
              // ATIVA não vira NOMINAL (422 CAMPANHA_AUDIENCIA_IMUTAVEL).
              disabled={ativa && t === 'NOMINAL' && tipoAtual !== 'NOMINAL'}
            >
              {AUDIENCIA_ROTULO[t]}
            </MenuItem>
          ))}
        </TextField>

        {valor.audienceType === 'CANAIS' && (
          <Box>
            <FormGroup aria-label="Canais da audiência">
              {CANAIS_COMERCIAIS.map((c) => (
                <FormControlLabel
                  key={c}
                  control={
                    <Checkbox
                      size="small"
                      checked={valor.audienciaCanais.includes(c)}
                      onChange={() => alternarCanal(c)}
                      disabled={encerrada}
                    />
                  }
                  label={CANAL_LABEL[c]}
                />
              ))}
            </FormGroup>
            <FormHelperText>Escolha ao menos um canal</FormHelperText>
          </Box>
        )}

        {valor.audienceType === 'EMPRESAS' && (
          <Box>
            <Autocomplete
              multiple
              size="small"
              options={empresas}
              value={selecionadas}
              disabled={encerrada}
              onChange={(_, v) => onChange({ ...valor, audienciaEmpresas: v.map((e) => e.id) })}
              getOptionLabel={(e) => e.nome}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="Nenhuma empresa"
              renderInput={(params) => (
                <TextField {...params} label="Empresas" placeholder="Buscar empresa" helperText="Escolha ao menos uma empresa" />
              )}
            />
            {erroEmpresas && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {erroEmpresas}
              </FormHelperText>
            )}
          </Box>
        )}

        {ativa && (
          <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
            Campanha ativa: a audiência só pode ser ampliada
          </Alert>
        )}
        {encerrada && (
          <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
            Campanha encerrada: a audiência não muda
          </Alert>
        )}
      </Stack>
    </Box>
  )
}

export default AudienciaCampos
